import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LgvStatesHelper } from '../../core/utility/lgv-states-helper';
import { LgvUserStateLicensingService } from '../../core/services/lgv-user-licensing.service';
import { BehaviorSubject, Observable, catchError, finalize, first, mergeMap, of } from 'rxjs';
import { LgvUserService } from '../../core/services/lgv-user.service';

@Component({
  selector: 'lgv-state-dropdown',
  templateUrl: './state-dropdown.component.html',
  styleUrls: ['./state-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StateDropdownComponent),
      multi: true
    }
  ]
})
export class StateDropdownComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  @Input() label: string = 'State';
  @Input() required: boolean = false;
  @Input() public: boolean = false;
  @Input() set disabled(value: boolean) {
    if (value) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }

  @Input() set control(value: FormControl) {
    console.log('StateDropdown: control set to', !!value);
    this._control = value;
  }

  get control(): FormControl {
    return this._control;
  }

  @Input() set states(value: { label: string, value: string }[]) {
    if (value && value.length > 0) {
      this._states = value;
    } else if (!this._userId) {
      this._states = LgvStatesHelper.getAllStates();
    }
  }

  @Input() set userId(value: string) {
    console.log('StateDropdown: userId changed to', value);
    this.lgvUserService.IsAdmin$.subscribe(isAdmin => {
      if (isAdmin) {
        value = ''
      }
    });
    if (!value) {
      console.log('StateDropdown: userId is null or undefined, using all states');
      this._userId = null;
      this._states = LgvStatesHelper.getAllStates();
      this.noLicensedStates.emit(false); // Emit that we have states (all states)
      return;
    }

    if (this._userId !== value) {
      this._userId = value;
      this.loadLicensedStates();
    }
  }

  get states(): { label: string, value: string }[] {
    return this._states;
  }

  get loading$(): Observable<boolean> {
    return this._loading.asObservable();
  }

  @Output() noLicensedStates = new EventEmitter<boolean>();

  private _states = LgvStatesHelper.getAllStates();
  private _userId: string;
  private _loading = new BehaviorSubject<boolean>(false);
  private _control: FormControl;
  private _initialLoad = true; // Flag to track initial load


  constructor(
    private userLicensingService: LgvUserStateLicensingService,
    private lgvUserService: LgvUserService
  ) {
    console.log('StateDropdown: Constructor called, userLicensingService:', !!this.userLicensingService);
  }

  ngOnInit() {
    console.log('StateDropdown: ngOnInit called, userId:', this._userId);
  }

  ngAfterViewInit(): void {
    console.log('StateDropdown: ngAfterViewInit called, control:', !!this.control);
    this.control?.valueChanges.subscribe(value => {
      console.log('StateDropdown: Control value changed to', value);
      this.onChange(value);
      this.onTouched();
    });
  }

  private loadLicensedStates() {
    if (!this._userId) {
      console.log('StateDropdown: No userId provided, using all states');
      return;
    }

    console.log('StateDropdown: Loading licensed states for userId', this._userId);
    this._loading.next(true);

    // Use the user service to get the agent snapshot
    this.lgvUserService.AgentSS$.pipe(
      first(),
      mergeMap(agentSS => {
        if (agentSS) {
          const isPreLicensed = agentSS.roles.includes('PreLicensedAgent');
          const ckUserId = agentSS.agentInfo?.ckUserId;

          // If pre-licensed and has CK, use CK's licenses
          if (isPreLicensed && ckUserId) {
            console.log('StateDropdown: User is pre-licensed, using CK states', ckUserId);
            return this.userLicensingService.getUserLicenses(ckUserId);
          }

          // Otherwise use the original user's licenses
          return this.userLicensingService.getUserLicenses(this._userId);
        } else {
          return this.userLicensingService.getPublicUserLicenses(this._userId);
        }
      }),
      catchError(error => {
        console.error('StateDropdown: Error loading licensed states:', error);
        this.noLicensedStates.emit(true);
        return of([]); // Return empty array on error
      }),
      finalize(() => this._loading.next(false))
    ).subscribe(licenses => {
      if (licenses.length === 0) {
        this._states = []
        this.noLicensedStates.emit(true);
        return;
      }

      // Filter to only active licenses and map to state codes
      const activeLicenses = licenses
        .filter(license => license.status === 'Approved')
        .map(license => license.state);

      // Update the states list with only licensed states
      if (activeLicenses.length > 0) {
        this._states = LgvStatesHelper.getAllStates()
          .filter(state => activeLicenses.includes(state.label));
        const currentValue = this._control.value;
        const currentState = LgvStatesHelper.getAllStates().find(state => state.value === currentValue);

        // Only set a new value if current state is not in active licenses
        if (!currentState || !activeLicenses.includes(currentState.label)) {
          this._control.setValue(this._states[0]?.value);
        }
      } else {
        this._states = []
        this.noLicensedStates.emit(true);
      }
      this.noLicensedStates.emit(this._states.length === 0);
    });
  }

  private onChange: any = () => { };
  private onTouched: any = () => { };

  writeValue(value: any): void {
    if (value !== undefined) {
      this.control.setValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.control.disable() : this.control.enable();
  }
} 